import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { Autorizacion, Objeto, Permiso, Permisos, PermisosDTO, Rol } from '../models/auth.models';

/**
 * Permisos del usuario, tal como los expone el backend en /autenticacion/permisos/.
 *
 * Los permisos son los de Django (view / view_massive / add / change / delete)
 * agrupados por modelo, así que la UI y la API se gobiernan con la misma fuente:
 * si el backend no deja listar paquetes, el frontend tampoco muestra el menú.
 */
@Injectable({ providedIn: 'root' })
export class AutorizacionService {
  private readonly http = inject(HttpClient);

  private readonly _permisos = signal<Permisos>({});
  private readonly _rol = signal<Rol | null>(null);

  readonly permisos = this._permisos.asReadonly();
  readonly rol = this._rol.asReadonly();

  async cargar(): Promise<void> {
    try {
      const dto = await firstValueFrom(this.http.get<PermisosDTO>('autenticacion/permisos/'));
      this._permisos.set(dto.permisos ?? {});
      this._rol.set(dto.rol ?? null);
    } catch {
      this.limpiar();
    }
  }

  limpiar(): void {
    this._permisos.set({});
    this._rol.set(null);
  }

  tienePermiso(objeto: Objeto, permiso: Permiso): boolean {
    return this._permisos()[objeto]?.includes(permiso) ?? false;
  }

  tienePermisos(objeto: Objeto, permisos: Permiso[]): boolean {
    return permisos.every((p) => this.tienePermiso(objeto, p));
  }

  tieneRol(roles: Rol[]): boolean {
    const rol = this._rol();
    return rol !== null && roles.includes(rol);
  }

  /** Evalúa una regla completa. Un arreglo de `permisos` significa OR entre opciones. */
  tieneAutorizacion(autorizacion: Autorizacion): boolean {
    if (autorizacion.roles && !this.tieneRol(autorizacion.roles)) return false;
    if (!autorizacion.permisos) return true;

    const opciones = Array.isArray(autorizacion.permisos)
      ? autorizacion.permisos
      : [autorizacion.permisos];

    return opciones.some((opcion) =>
      Object.entries(opcion).every(([objeto, permisos]) =>
        this.tienePermisos(objeto as Objeto, (permisos ?? []) as Permiso[]),
      ),
    );
  }
}
